import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  IStationeryReturnDetailedItem,
  IStationeryReturnListItem,
} from 'src/types/stationery-return';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  returns: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IStationeryReturnListItem[];
  };
  return: IStationeryReturnDetailedItem | null;
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  returns: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  return: null,
};

const stationeryReturnSlice = createSlice({
  name: 'stationeryReturn',
  initialState,
  reducers: {
    removeStationeryReturn: (state, action) => {
      const { returnId } = action.payload;

      const index = state.returns.items.findIndex((item) => item._id === returnId);

      if (index > -1) {
        action.payload.index = index;
        state.returns.items = state.returns.items.filter((item) => item._id !== returnId);
      }
    },
    rollbackStationeryReturnDeletion: (state, action) => {
      const { item, index } = action.payload;

      state.returns.items.splice(index, 0, item);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStationeryReturns.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStationeryReturns.fulfilled, (state, action) => {
        state.status = 'success';
        state.returns = action.payload;
      })
      .addCase(getStationeryReturns.rejected, (state, action) => {
        state.returns = initialState.returns;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getStationeryReturnById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStationeryReturnById.fulfilled, (state, action) => {
        state.status = 'success';
        state.return = action.payload;
        state.error = initialState.error;
      })
      .addCase(getStationeryReturnById.rejected, (state, action) => {
        state.return = initialState.return;
        state.status = 'failure';
        state.error = action.error.message;
      });
  },
});

export default stationeryReturnSlice.reducer;

export const { removeStationeryReturn, rollbackStationeryReturnDeletion } =
  stationeryReturnSlice.actions;

export const getStationeryReturns = createAsyncThunk(
  'stationeryReturn/paginated',
  async (params: {
    page: number;
    limit: number;
    generalSearch?: string | null;
    issuedBy?: string | null;
    receivedBy?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    sortBy?: string | null;
    sort?: 'asc' | 'desc' | null;
  }) => {
    const { page, limit, generalSearch, issuedBy, receivedBy, startDate, endDate, sortBy, sort } =
      params;

    try {
      const response = await axios.get(
        `stationery/return?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&issuedBy=${issuedBy ?? ''}&receivedBy=${receivedBy ?? ''}&startDate=${
          startDate ? startDate.toISOString() : ''
        }&endDate=${endDate ? endDate.toISOString() : ''}&sortBy=${sortBy}&sort=${sort ?? ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getStationeryReturnById = createAsyncThunk(
  'stationeryReturn/getById',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`stationery/return/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deleteStationeryReturn = createAsyncThunk(
  'stationeryReturn/delete',
  async (params: { item: IStationeryReturnListItem; index: number }, { dispatch }) => {
    const { item, index } = params;

    try {
      const response = await axios.delete(`stationery/return/${item._id}`);
      return response.data.data;
    } catch (err) {
      dispatch(rollbackStationeryReturnDeletion({ item, index }));
      throw err;
    }
  }
);

export interface TStationeryReturnItemInput {
  sourceDispatchItem: string;
  quantity: number;
  // Employee physically returning this specific line — per-line, not
  // per-return.
  issuedBy: string;
  // Create-only — a return with damaged lines can't be edited (the backend
  // refuses it since the auto-created linked damage dispatch has no
  // diffing/re-sync logic).
  damaged?: boolean;
  supplierDamagedQuantity?: number;
  staffDamagedQuantity?: number;
}

export interface TCreateStationeryReturn {
  receivedBy: string;
  returnDate: Date;
  remarks: string | null;
  items: TStationeryReturnItemInput[];
}

export type TUpdateStationeryReturn = TCreateStationeryReturn;

export const createStationeryReturn = createAsyncThunk(
  'stationeryReturn/create',
  async (params: { returnTransaction: TCreateStationeryReturn }) => {
    const { returnTransaction } = params;
    const response = await axios.post('stationery/return', returnTransaction);
    return response.data.data;
  }
);

export const updateStationeryReturn = createAsyncThunk(
  'stationeryReturn/update',
  async (params: { returnId: string; returnTransaction: TUpdateStationeryReturn }) => {
    const { returnId, returnTransaction } = params;
    const response = await axios.put(`stationery/return/${returnId}`, returnTransaction);
    return response.data.data;
  }
);
